import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// ── Models ──────────────────────────────────────────────────────────────────

export type AuditAction = 'CREATED' | 'UPDATED' | 'DELETED';

export type AuditValueType =
  | 'CURRENCY'
  | 'DATE'
  | 'BOOLEAN'
  | 'ENUM'
  | 'NUMBER'
  | 'REFERENCE'
  | 'STRING';

export interface AuditTypedValue {
  display: string;
  type: AuditValueType;
  raw: any;
}

export interface AuditChange {
  field: string;
  oldValue: AuditTypedValue | null;
  newValue: AuditTypedValue | null;
}

export interface AuditRow {
  commitId: string;
  timestamp: string;
  user: string;
  entityType: string;
  entityLabel: string;
  entityId: string;
  action: AuditAction;
  summary: string;
}

export interface AuditFeedPage {
  rows: AuditRow[];
  totalCount: number;
  page: number;
  size: number;
}

export interface AuditTimelineEntry {
  commitId: string;
  timestamp: string;
  user: string;
  action: AuditAction;
  summary: string;
  changes: AuditChange[];
}

export interface RelatedNode {
  entityType: string;
  entityId: string;
  label: string;
  businessId?: string | null;
  status?: string | null;
  deleted?: boolean;
  children: RelatedNode[];
}

export interface RelatedRecordsTree {
  nodes: RelatedNode[];
  truncated: boolean;
}

export interface AuditFeedParams {
  entityType?: string;
  entityId?: string;
  user?: string;
  action?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
  search?: string;
  location?: string;
  state?: string;
}

// ── Service ──────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class AuditService {
  private baseUrl = environment.apiUrl + '/internal/audit';
  private tokenKey = 'token';

  constructor(private http: HttpClient) {}

  /**
   * Fetches a paginated, filtered page of audit rows.
   */
  getFeed(params: AuditFeedParams): Observable<AuditFeedPage> {
    const token = localStorage.getItem(this.tokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    let httpParams = new HttpParams();
    if (params.entityType) httpParams = httpParams.set('entityType', params.entityType);
    if (params.entityId)   httpParams = httpParams.set('entityId', params.entityId);
    if (params.user)       httpParams = httpParams.set('user', params.user);
    if (params.action)     httpParams = httpParams.set('action', params.action);
    if (params.dateFrom)   httpParams = httpParams.set('dateFrom', params.dateFrom);
    if (params.dateTo)     httpParams = httpParams.set('dateTo', params.dateTo);
    if (params.search)     httpParams = httpParams.set('search', params.search);
    if (params.location)   httpParams = httpParams.set('location', params.location);
    if (params.state)      httpParams = httpParams.set('state', params.state);
    if (params.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params.size != null) httpParams = httpParams.set('size', String(params.size));

    return this.http.get<AuditFeedPage>(this.baseUrl, { headers, params: httpParams });
  }

  /**
   * Fetches the chronological timeline for a single entity.
   */
  getTimeline(entityType: string, entityId: string): Observable<AuditTimelineEntry[]> {
    const token = localStorage.getItem(this.tokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    const path = `${this.baseUrl}/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`;
    return this.http.get<AuditTimelineEntry[]>(path, { headers });
  }

  /**
   * Fetches the tree of records related to a single entity.
   */
  getRelated(entityType: string, entityId: string): Observable<RelatedRecordsTree> {
    const token = localStorage.getItem(this.tokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    const path = `${this.baseUrl}/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/related`;
    return this.http.get<RelatedRecordsTree>(path, { headers });
  }

  /**
   * Triggers a CSV download of the filtered feed (full set, no paging).
   * Returns an Observable so the caller can attach an error handler.
   * On success: the blob is received, an object URL is created, a temp anchor
   * click triggers the browser download, then the URL is revoked.
   */
  exportCsv(params: Omit<AuditFeedParams, 'page' | 'size'>): Observable<void> {
    const token = localStorage.getItem(this.tokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    let httpParams = new HttpParams();
    if (params.entityType) httpParams = httpParams.set('entityType', params.entityType);
    if (params.entityId)   httpParams = httpParams.set('entityId', params.entityId);
    if (params.user)       httpParams = httpParams.set('user', params.user);
    if (params.action)     httpParams = httpParams.set('action', params.action);
    if (params.dateFrom)   httpParams = httpParams.set('dateFrom', params.dateFrom);
    if (params.dateTo)     httpParams = httpParams.set('dateTo', params.dateTo);
    if (params.search)     httpParams = httpParams.set('search', params.search);
    if (params.location)   httpParams = httpParams.set('location', params.location);
    if (params.state)      httpParams = httpParams.set('state', params.state);

    // Returned as an Observable so the caller can attach an error handler and surface failures.
    return new Observable<void>((observer) => {
      const subscription = this.http
        .get(`${this.baseUrl}/export`, {
          headers,
          params: httpParams,
          responseType: 'blob',
          observe: 'response',
        })
        .subscribe({
          next: (response) => {
            const blob = response.body;
            if (!blob || blob.size === 0) {
              observer.error(new Error('Empty CSV response'));
              return;
            }
            this.triggerBrowserDownload(blob, 'audit-log.csv');
            observer.next();
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
      // Teardown: if the caller unsubscribes (e.g. the component is destroyed mid-request),
      // cancel the in-flight HTTP request so it doesn't leak or do needless work.
      return () => subscription.unsubscribe();
    });
  }

  /**
   * Triggers a browser file download for a blob: creates a temporary object URL, clicks a
   * hidden anchor, then revokes the URL. Isolated as a DOM-only step so exportCsv's HTTP logic
   * stays focused (the same in-service download pattern document.service uses).
   */
  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 100);
  }
}
